import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../api/apiServices"; // ← adjust this import path

export default function ForgotPassword() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isValid =
    email.trim().length > 5 &&
    email.includes("@") &&
    !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();

    setMessage("");
    setError("");
    setLoading(true);

    try {
      await apiClient.post("/auth/forgot-password", {
        email: email.trim().toLowerCase(),
      });

      setMessage(
        "An OTP has been sent to your email. Please check your inbox and spam folder."
      );

      // Optional: automatically redirect to login after showing message
       setTimeout(() => navigate("/reset-password", { state: { email: email.trim().toLowerCase() } }), 5000);

    } catch (err) {
      console.error("Forgot password request failed:", err);

      let errMsg = "Something went wrong. Please try again.";

      if (err.response?.data?.message) {
        errMsg = err.response.data.message;
      } else if (err.response?.status === 404 || err.response?.status === 400) {
        errMsg = "No account found with that email address.";
      }

      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  // Animation variants (consistent with login & signup)
  const pageVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  };

  const inputVariants = {
    focus: { scale: 1.02, boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.3)" },
  };

  const buttonVariants = {
    valid: { scale: [1, 1.03, 1], transition: { duration: 1.5, repeat: Infinity } },
    invalid: { scale: 1 },
  };

  const errorVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={pageVariants}
      className="min-h-screen bg-[#F5F6FA] flex flex-col px-5 sm:px-6 pt-6 pb-10"
    >
      {/* Top Bar */}
      <div className="flex justify-between items-center mb-8">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate(-1)}
          className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition"
        >
          <ArrowLeft size={24} className="text-gray-700" />
        </motion.button>

        <motion.span
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate("/signup")}
          className="text-sm font-semibold text-blue-600 hover:text-blue-800 cursor-pointer transition"
        >
          Sign up
        </motion.span>
      </div>

      {/* Title & Subtitle */}
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Reset Password</h1>
      <p className="text-base text-gray-600 mb-10">
        Enter your email address and we'll send you an OTP to reset your password
      </p>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6 flex-1">
        {/* Email Field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email address
          </label>
          <motion.input
            type="email"
            placeholder="john@example.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError("");
              setMessage("");
            }}
            className={`w-full rounded-full px-5 py-4 bg-white border-2 transition-all duration-200 outline-none text-gray-900 placeholder-gray-400 shadow-sm focus:shadow-md ${
              error
                ? "border-red-500 focus:ring-red-200"
                : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
            }`}
            whileFocus="focus"
            variants={inputVariants}
          />
        </div>

        {/* Feedback Messages */}
        <AnimatePresence>
          {error && (
            <motion.p
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={errorVariants}
              className="text-sm text-red-600 bg-red-50 p-3 rounded-xl text-center border border-red-200"
            >
              {error}
            </motion.p>
          )}

          {message && (
            <motion.p
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={errorVariants}
              className="text-sm text-green-700 bg-green-50 p-3 rounded-xl text-center border border-green-200"
            >
              {message}
            </motion.p>
          )}
        </AnimatePresence>

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={!isValid}
          variants={buttonVariants}
          animate={isValid ? "valid" : "invalid"}
          whileTap={{ scale: 0.97 }}
          className={`w-full py-4 rounded-full font-semibold text-base shadow-md transition-all duration-300 ${
            isValid
              ? "bg-[#344054] text-white hover:bg-[#2a3745]"
              : "bg-[#D0D5DD] text-gray-500 cursor-not-allowed"
          }`}
        >
          {loading ? "Continue..." : "Send Reset OTP"}
        </motion.button>

        {/* Back to Login */}
        <div className="text-center mt-6">
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium transition"
          >
            Back to Log in
          </button>
        </div>
      </form>

      {/* Bottom Branding */}
      <div className="text-center pb-8 text-sm text-gray-500 font-medium mt-6">
        DataBling
      </div>
    </motion.div>
  );
}